import React, { useEffect, useState } from "react"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Loader2 } from "lucide-react"
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts"
import { useToast } from "src/components/ui/toast"
import { fetchWebsiteEnquiries } from "src/features/analytics/api/analyticsApi"
import { useReport } from "src/features/analytics/hooks/useReport"
import { presetToRange, CHART_COLORS, formatNumber } from "src/features/analytics/constants"
import ReportFilters from "src/features/analytics/components/ReportFilters"
import KpiCard from "src/features/analytics/components/KpiCard"
import EmptyReportState from "src/features/analytics/components/EmptyReportState"

const STATE_LABELS = { new: "New", rental: "Rental", resale: "Resale", unknown: "Unknown" }

export default function WebsiteEnquiriesReportPage() {
  const { toast } = useToast()
  const [preset, setPreset] = useState("30d")
  const [filters, setFilters] = useState(presetToRange("30d"))
  const { data, loading, fetch } = useReport(fetchWebsiteEnquiries)

  useEffect(() => {
    fetch(filters).catch((err) => {
      if (err?.response?.status === 403) {
        toast({ variant: "destructive", title: "Error", description: "You don't have permission to view reports." })
      }
    })
  }, [filters.from, filters.to])

  const totals = data?.totals

  return (
    <div className="mx-auto max-w-[1120px]">
      <ReportFilters
        filters={filters}
        onFiltersChange={setFilters}
        preset={preset}
        onPresetChange={setPreset}
      />

      {loading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !totals || totals.enquiries === 0 ? (
        <EmptyReportState message="No website enquiries in this period yet. Publish listings and share your /p/ links to start capturing leads." />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard label="Enquiries" value={formatNumber(totals.enquiries)} />
            <KpiCard label="Assigned" value={formatNumber(totals.assigned)} />
            <KpiCard label="Converted" value={formatNumber(totals.converted)} />
            <KpiCard label="Conversion" value={`${totals.conversion_pct}%`} />
          </div>

          {/* Enquiries over time */}
          {data.by_day?.length > 0 && (
            <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 text-[13px] font-medium text-slate-700">Enquiries over time</div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.by_day}>
                  <defs>
                    <linearGradient id="enqFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Area
                    type="monotone" dataKey="count" name="Enquiries"
                    stroke={CHART_COLORS.primary} strokeWidth={2} fill="url(#enqFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <BreakdownTable
              title="By offer"
              rows={data.by_offer_state}
              labelKey="offer_state"
              format={(v) => STATE_LABELS[v] || v}
            />
            <BreakdownTable title="By city" rows={data.by_city} labelKey="city" />
          </div>

          {data.top_listings?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 text-[13px] font-medium text-slate-700">Top listings by enquiries</div>
              <Table className="text-[13px]">
                <TableHeader className="bg-slate-50 text-left text-slate-500">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 font-medium text-slate-500">Listing</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Enquiries</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_listings.map((row) => (
                    <TableRow key={row.token}>
                      <TableCell className="py-2.5 text-slate-900">
                        <a
                          className="text-blue-600 hover:underline"
                          href={`/p/${row.token}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {row.title}
                        </a>
                      </TableCell>
                      <TableCell className="py-2.5 text-right">{row.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function BreakdownTable({ title, rows = [], labelKey, format = (v) => v }) {
  if (!rows.length) return null
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 text-[13px] font-medium text-slate-700">{title}</div>
      <Table className="text-[13px]">
        <TableBody>
          {rows.map((row) => (
            <TableRow key={String(row[labelKey])}>
              <TableCell className="py-2.5 text-slate-900 capitalize">{format(row[labelKey])}</TableCell>
              <TableCell className="py-2.5 text-right">{row.count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
